import React, { createContext, ReactNode, useContext, useRef, useState } from "react";
import { Dialog, DialogProps } from "@mui/material";

export type TDialog = {
  name: string;
  open: boolean;
  onClose?: () => void;
  children: ReactNode;
  afterClosing?: () => void;
  maxWidth?: DialogProps["maxWidth"];
  fullWidth?: DialogProps["fullWidth"];
};

export const DialogContextV2 = createContext({
  createDialog: (option: Omit<TDialog, "open">) => {},
  closeDialog: (dialogName: string) => {},
});

export const useDialog = () => useContext(DialogContextV2);

function DialogContainer({
  children,
  onClose,
  ...dialogProps
}: { onClose?: () => void } & Pick<DialogProps, "maxWidth" | "children" | "open" | "fullWidth">) {
  return (
    <Dialog onClose={onClose ?? (() => {})} {...dialogProps}>
      {children}
    </Dialog>
  );
}

export function DialogProvider({ children }: { children: ReactNode }) {
  const [dialogs, setDialogs] = useState<TDialog[]>([]);

  const createDialog = (option: TDialog) => {
    const dialog = { ...option, open: true };
    setDialogs((dialogs) => [...dialogs, dialog]);
  };

  const closeDialog = (name: string) => {
    setDialogs((dialogs) => {
      const copy = [...dialogs];
      const remainingDialogs = copy.filter((dialog) => dialog.name !== name);
      const dialogsToClose = copy.filter((dialog) => dialog.name === name);
      dialogsToClose.forEach((dialog) => (dialog.afterClosing ? dialog.afterClosing() : null));

      return remainingDialogs;
    });
  };

  const contextValue = useRef({ createDialog, closeDialog });

  return (
    <DialogContextV2.Provider value={contextValue.current}>
      {children}
      {dialogs.map((dialog, i) => {
        return <DialogContainer key={i} {...dialog} />;
      })}
    </DialogContextV2.Provider>
  );
}
